import {
  Add,
  Close,
  FileDownloadOutlined,
  Lock,
  Save,
  Settings,
} from "@mui/icons-material";
import { Button, IconButton, Menu, MenuItem } from "@mui/material";
import { Box } from "@mui/system";
import { App, Plugin, Route, View } from "@pimo/pimo-app-builder";
import {
  ChipCell,
  DateCell,
  DerivePMTableProps,
  DragAndDropLayout,
  DragDropLayoutTabDefinition,
  generateTableHeaderEntriesWithSorting,
  PMButton,
  PMPopUp,
  PMTableColumnActionEventPayload,
  PMTableEventNames,
  PMTableEventPayload,
  PMTableSortEventPayload,
  ProgressBarCell,
  SortDirection,
  StatusIndicatorCell,
  TextCardCell,
} from "@pimo/pimo-components";
import PMTable from "@pimo/pimo-components/src/lib/pm-components/pm-table/pm-table";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import {
  BasicItem,
  Configuration,
  Project,
  ReportingItem,
} from "product-test-types";
import {
  canUserPerformOperation,
  getContrastColor,
  getCustomFieldTableColumnFieldName,
  getLegacyCustomFieldTableColumnFieldName,
} from "product-test-utils";
import { generatePath } from "react-router-dom";

import { TestAppState } from "../app";
import {
  type FilterData,
  SearchAndFilter,
} from "../components/project-filter-dialog";
import { TrackingItemDetailsModal } from "../components/tracking-item-modal";
import { APP_ROUTES } from "../constants";
import { buildFormRoute } from "../form/build-form-route";
import { buildDimensionFormRoute } from "../form/build-form-route-dimension";
import { buildMaturityFormRoute } from "../form/build-form-route-maturity";
import { buildTrackingItemFormRoute } from "../form/build-tracking-item-form-route";
import { downloadPdf, getRequestedPdfTabIndex } from "../helpers/download-pdf";
import { downloadAttachment } from "../helpers/fetch-attachments";
import { deleteBasicItem } from "../helpers/fetch-basic-items";
import { fetchEntityConfiguration } from "../helpers/fetch-entity-configurations";
import { downloadProjectExcelExport } from "../helpers/fetch-excel-export";
import { fetchProject, updateProject } from "../helpers/fetch-project";
import {
  fetchTableConfiguration,
  saveTableConfiguration,
} from "../helpers/fetch-table-configuration";
import { createViewConfiguration } from "../helpers/fetch-view-configuration";
import {
  applyColumnOrderToHeadersForReportPlugin,
  REPORT_TABLE_FIELD_ORDER,
} from "../helpers/pages-helpers/report-plugin-helpers";
import { showSnackbar } from "../helpers/show-snackbar";
import { theme } from "../theme";
import {
  getDropdownColorValue,
  getDropdownLabel,
} from "../utils/dropdown-options";
import { formatDate } from "../utils/format-date";
import { getLocalizedCardCopy } from "../utils/get-localized-card-copy";
import { getLocalizedTableColumnLabel } from "../utils/get-localized-table-column-label";
import { getLockedByMessage } from "../utils/get-locked-by-message";
import {
  getItemName,
  getPluralOfItemName,
} from "../utils/get-plural-of-item-name";
import { getProjectName } from "../utils/get-plural-of-project-name";
import { sortReportingItems } from "../utils/sort-table-data";
import { REPORT_CARDS } from "./report";
import { addCustomFieldCards } from "./report/custom-fields";

export function isMaturityReportComplete(project?: Project): boolean {
  const dimensionCompletion = project?._calculated?.dimensionFormCompletion;
  if (dimensionCompletion && Object.keys(dimensionCompletion).length > 0) {
    return Object.values(dimensionCompletion).every(
      (status) => status === "completed"
    );
  }

  const reportingItems = project?.reportingItems ?? [];

  return (
    reportingItems.length > 0 &&
    reportingItems.every((reportingItem) =>
      Boolean(reportingItem.degreeOfCompletion)
    )
  );
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ReportPluginState {}

export class ReportPlugin implements Plugin<
  TestAppState,
  TestAppState,
  "id" | "viewname"
> {
  route?: Route<"id" | "viewname", TestAppState>;
  private view?: View<TestAppState, DragAndDropLayout>;
  private app?: App<TestAppState>;
  private downloadMenuAnchor?: HTMLElement | null;

  onRegister(app: App<TestAppState>): void {
    this.app = app;
    if (!this.app) {
      return;
    }

    const navigateToCurrentReport = () => {
      if (!this.app || !this.view) {
        return;
      }

      const resourceLockPlugin =
        this.app?.getPluginByName<ResourceLockPlugin<TestAppState>>(
          "ResourceLockPlugin"
        );

      const { currentReport } = this.app.getAppState();
      resourceLockPlugin?.lock(`project-update-${currentReport?.project?.id}`);
      this.app.patchAppState({
        editForm: { currentProject: currentReport?.project },
      });
      this.app.navigate(
        generatePath(APP_ROUTES.editReport, {
          id: String(currentReport?.project?.id),
          viewname:
            getLocalizedMessage(
              this.app?.getAppState().language || "en",
              "dashboard"
            ) || "dashboard",
        })
      );
    };

    this.view = app.createView({
      name:
        getLocalizedMessage(
          this.app?.getAppState().language || "en",
          "reports"
        ) || "Reports",
      layout: new DragAndDropLayout(),
    });

    this.view.mapLayoutState(
      ({
        reportLayout,
        currentReport,
        settings,
        userProfile,
        showDeleteTrackingItemDialog,
        pendingDeleteTrackingItemId,
        showTrackingItemDetailsModal,
        selectedTrackingItem,
        showMaturitySubmissionDialog,
        showNoDeliverablesDialog,
        language,
        reportTableConfiguration,
        lockDetails,
      }) => {
        const confirmationPopupType =
          reportTableConfiguration?.confirmationPopupType;

        const resourceLockPlugin =
          this.app?.getPluginByName<ResourceLockPlugin<TestAppState>>(
            "ResourceLockPlugin"
          );

        const currentProjectLock =
          lockDetails?.projectLocks?.[currentReport?.project?.id ?? ""];
        const isCurrentProjectEditLocked =
          currentProjectLock?.edit?.isLocked &&
          currentProjectLock?.edit?.lockedBy !== userProfile?.email;
        const isCurrentProjectUpdateLocked =
          currentProjectLock?.update?.isLocked &&
          currentProjectLock?.update?.lockedBy !== userProfile?.email;

        const selectedItemLock =
          lockDetails?.itemLocks?.[
            `item-${selectedTrackingItem?.basicItem?.id}`
          ];

        let tabs;
        if (reportLayout?.tabs && reportLayout.tabs.length > 0) {
          tabs = reportLayout.tabs;
        } else {
          tabs = [
            {
              id: 0,
              title: getLocalizedMessage(language, "tab1") || "Tab 1",
              rows: [{ id: 0, columnCount: 3, columns: [] }],
            },
          ];
        }

        const localizedLanguage = language || "en";
        const activeTabIndex =
          getRequestedPdfTabIndex() ?? reportLayout?.activeTabIndex ?? 0;
        const isMaturityUseCase =
          settings?.configuration?.useCase === "maturity";
        const isMaturityReportSubmitted = Boolean(
          currentReport?.project?.submitter
        );
        const isMaturityReportCompleteValue = isMaturityReportComplete(
          currentReport?.project
        );
        const canAddTrackingItem = canUserPerformOperation({
          userGroups: userProfile?.groups || [],
          operation: "edit",
          object: "item",
          entityId: currentReport?.project?.id,
          client: settings?.configuration?.client,
          tool: settings?.configuration?.tool,
        });

        return {
          tabs,
          activeTabIndex,
          onTabChange: (tabIndex: number) => {
            this.app?.patchAppState({
              reportLayout: {
                ...(this.app?.getAppState().reportLayout ?? {}),
                tabs,
                displayTabs: true,
                canEdit: reportLayout?.canEdit ?? true,
                activeTabIndex: tabIndex,
              },
            });
          },
          canEdit: canUserPerformOperation({
            userGroups: userProfile?.groups || [],
            lowestLevelAccessRequiredToPerformThisOperation: "admin",
            client: settings?.configuration?.client,
            tool: settings?.configuration?.tool,
          }),
          displayTabs: true,
          language: localizedLanguage,
          rightSlot: (
            <>
              <Box
                className="no-print"
                data-tourid="update-report"
                sx={{
                  display: "flex",
                  gap: 1,
                }}
              >
                {canUserPerformOperation({
                  userGroups: userProfile?.groups || [],
                  operation: "edit",
                  object: "project",
                  entityId: currentReport?.project?.id,
                  lowestLevelAccessRequiredToPerformThisOperation: "program",
                  client: settings?.configuration?.client,
                  tool: settings?.configuration?.tool,
                }) && (
                  <PMButton
                    tooltip={
                      isCurrentProjectEditLocked
                        ? getLockedByMessage(
                            "project",
                            language,
                            currentProjectLock.edit
                          )
                        : undefined
                    }
                    icon={isCurrentProjectEditLocked ? <Lock /> : undefined}
                    size="medium"
                    onClick={async () => {
                      if (!this.app || !currentReport?.project?.id) return;

                      await resourceLockPlugin?.lock(
                        `project-${currentReport.project.id}`
                      );

                      this.app.patchAppState({
                        editForm: {
                          currentProject: currentReport.project,
                        },
                      });

                      this.app.navigate(
                        generatePath(APP_ROUTES.projectDialog, {
                          id: currentReport.project.id,
                          isFromOverview: "false",
                        })
                      );
                    }}
                    text={`${getLocalizedMessage(language, "editProject", {
                      projectName:
                        settings?.configuration?.projectName ??
                        getLocalizedMessage(language, "project") ??
                        "Project",
                    })} `}
                    variant={
                      isCurrentProjectEditLocked ? "disabled" : "contained"
                    }
                  />
                )}
                {canUserPerformOperation({
                  userGroups: userProfile?.groups || [],
                  entityId: currentReport?.project?.id,
                  operation: "report",
                  object: "project",
                  client: settings?.configuration?.client,
                  tool: settings?.configuration?.tool,
                }) &&
                  settings?.configuration?.isReportingEnabled && (
                    <PMButton
                      size="medium"
                      tooltip={
                        isCurrentProjectUpdateLocked
                          ? getLockedByMessage(
                              "project-update",
                              language,
                              currentProjectLock.update
                            )
                          : undefined
                      }
                      icon={isCurrentProjectUpdateLocked ? <Lock /> : undefined}
                      onClick={() => {
                        if (isMaturityUseCase) {
                          this.app?.patchAppState({
                            showMaturitySubmissionDialog: true,
                          });
                          return;
                        }

                        if (!currentReport?.project?.reportingItems?.length) {
                          this.app?.patchAppState({
                            showNoDeliverablesDialog: true,
                          });
                          return;
                        }

                        navigateToCurrentReport();
                      }}
                      text={
                        isMaturityUseCase
                          ? isMaturityReportSubmitted
                            ? "Submitted"
                            : "Submit assessment"
                          : getLocalizedMessage(language, "updateReport") ||
                            "Update Report"
                      }
                      variant={
                        isCurrentProjectUpdateLocked ||
                        (isMaturityUseCase &&
                          (!isMaturityReportCompleteValue ||
                            isMaturityReportSubmitted))
                          ? "disabled"
                          : "report"
                      }
                    />
                  )}
                {canAddTrackingItem && (
                  <PMButton
                    size="medium"
                    variant="white"
                    icon={<Add />}
                    text={
                      getLocalizedMessage(language, "addDeliverableShort") ||
                      "Add Deliverable"
                    }
                    onClick={() => {
                      this.app?.navigate(
                        generatePath(APP_ROUTES.trackingItemDialog, {
                          id: currentReport?.project?.id ?? "",
                          "tracking-item-id": "new-tracking-item",
                          viewname: "dashboard",
                        })
                      );
                    }}
                  />
                )}
                <IconButton
                  onClick={(event) => {
                    this.downloadMenuAnchor = event.currentTarget;
                    this.app?.patchAppState({});
                  }}
                  sx={{ ml: 1 }}
                >
                  <FileDownloadOutlined />
                </IconButton>
              </Box>
              <PMPopUp
                open={Boolean(showNoDeliverablesDialog)}
                title={
                  getLocalizedMessage(language, "noDeliverablesTitle") ||
                  "No Deliverables Available"
                }
                description={
                  getLocalizedMessage(
                    language,
                    canAddTrackingItem
                      ? "noDeliverablesDescription"
                      : "noDeliverablesPermissionDescription"
                  ) ||
                  (canAddTrackingItem
                    ? "To update the report, at least one deliverable is required. Add a deliverable first."
                    : "To update the report, at least one deliverable is required. Please contact a user with the required permissions.")
                }
                onClose={() =>
                  this.app?.patchAppState({ showNoDeliverablesDialog: false })
                }
                maxWidth="sm"
                cancelButtonText={
                  canAddTrackingItem
                    ? getLocalizedMessage(language, "cancel") || "Cancel"
                    : undefined
                }
                cancelButtonColor="white"
                cancelButtonFireEvent={() =>
                  this.app?.patchAppState({ showNoDeliverablesDialog: false })
                }
                confirmationButtonText={
                  getLocalizedMessage(
                    language,
                    canAddTrackingItem ? "addDeliverable" : "understood"
                  ) || (canAddTrackingItem ? "Add Deliverable" : "Got it")
                }
                confirmationButtonColor="blue"
                confirmationButtonFireEvent={() => {
                  this.app?.patchAppState({ showNoDeliverablesDialog: false });
                  if (canAddTrackingItem) {
                    this.app?.navigate(
                      generatePath(APP_ROUTES.trackingItemDialog, {
                        id: currentReport?.project?.id ?? "",
                        "tracking-item-id": "new-tracking-item",
                        viewname: "dashboard",
                      })
                    );
                  }
                }}
              />
              <PMPopUp
                open={Boolean(showMaturitySubmissionDialog)}
                title="Submit maturity assessment?"
                description="All activities have been completed and saved. By submitting the assessment, the overall report will be finalized and marked as Submitted. Please make sure all information is correct before continuing."
                onClose={() =>
                  this.app?.patchAppState({
                    showMaturitySubmissionDialog: false,
                  })
                }
                maxWidth="sm"
                cancelButtonText="Cancel"
                cancelButtonColor="white"
                cancelButtonFireEvent={() =>
                  this.app?.patchAppState({
                    showMaturitySubmissionDialog: false,
                  })
                }
                confirmationButtonText="Submit assessment"
                confirmationButtonColor="blue"
                confirmationButtonFireEvent={async () => {
                  const project = currentReport?.project;
                  if (!project?.id || !isMaturityReportComplete(project)) {
                    return;
                  }

                  const result = await updateProject(
                    project.id,
                    project,
                    true,
                    false,
                    true
                  );
                  if ("validationErrors" in result) {
                    return;
                  }

                  this.app?.patchAppState({
                    currentReport: { project: result },
                    showMaturitySubmissionDialog: false,
                  });
                }}
              />
              <Menu
                anchorEl={this.downloadMenuAnchor}
                open={Boolean(this.downloadMenuAnchor)}
                onClose={() => {
                  this.downloadMenuAnchor = null;
                  this.app?.patchAppState({});
                }}
              >
                <MenuItem
                  onClick={() => {
                    this.downloadMenuAnchor = null;
                    this.app?.patchAppState({});
                    void downloadPdf().catch((error: unknown) => {
                      console.error("PDF export failed", error);
                      if (this.app) {
                        showSnackbar(
                          this.app as never,
                          getLocalizedMessage(
                            language,
                            "generic.internalError"
                          ) || "Could not generate the PDF.",
                          "error"
                        );
                      }
                    });
                  }}
                >
                  {getLocalizedMessage(language, "download.pdf") ||
                    "Download PDF"}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    const projectId = currentReport?.project?.id;
                    if (projectId) {
                      downloadProjectExcelExport(projectId);
                    }
                    this.downloadMenuAnchor = null;
                    this.app?.patchAppState({});
                  }}
                  sx={{
                    color: "text.primary",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  {getLocalizedMessage(language, "download.excel") ||
                    "Download Excel"}
                </MenuItem>
              </Menu>
              {/* Inline confirmation dialog so it renders within the view rightSlot */}
              <PMPopUp
                open={!!showDeleteTrackingItemDialog}
                title={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.title.confirmDelete"
                  ) || "Confirm Deletion"
                }
                description={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.message.genericConfirm",
                    { itemName: `${settings?.configuration?.itemName}` }
                  ) ||
                  `Are you sure you want to delete this ${settings?.configuration?.itemName}? This action cannot be undone.`
                }
                onClose={() => {
                  this.app?.patchAppState({
                    showDeleteTrackingItemDialog: false,
                    pendingDeleteTrackingItemId: undefined,
                  });
                }}
                maxWidth="sm"
                cancelButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.cancel"
                  ) || "Cancel"
                }
                cancelButtonFireEvent={() => {
                  this.app?.patchAppState({
                    showDeleteTrackingItemDialog: false,
                    pendingDeleteTrackingItemId: undefined,
                  });
                }}
                cancelButtonColor="white"
                confirmationButtonColor="error"
                confirmationButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.delete"
                  ) || "Delete"
                }
                confirmationButtonFireEvent={async () => {
                  const basicItemId = pendingDeleteTrackingItemId;
                  if (basicItemId) {
                    try {
                      await deleteBasicItem(basicItemId);
                      if (currentReport?.project?.id) {
                        const updatedProject = await fetchProject({
                          id: currentReport.project.id,
                        });
                        this.app?.patchAppState({
                          currentReport: updatedProject,
                        });
                      }
                    } catch {
                      // ignore errors for now
                    }
                  }

                  this.app?.patchAppState({
                    showDeleteTrackingItemDialog: false,
                    pendingDeleteTrackingItemId: undefined,
                  });
                }}
              />
              {/* Confirmation dialog for saving layout changes across report pages */}
              <PMPopUp
                open={
                  !!this.app?.getAppState().showConfirmSaveReportLayoutDialog
                }
                title={
                  getLocalizedMessage(
                    language || "en",
                    "saveLayoutDialog.title"
                  ) || "Save layout changes"
                }
                description={
                  getLocalizedMessage(
                    language || "en",
                    "saveLayoutDialog.message"
                  ) ||
                  "When saving the layout, all other report pages will be adapted as well. Do you want to save these changes for all reports?"
                }
                onClose={() => {
                  this.app?.patchAppState({
                    showConfirmSaveReportLayoutDialog: false,
                  });
                }}
                maxWidth="sm"
                cancelButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.cancel"
                  ) || "Cancel"
                }
                cancelButtonFireEvent={() => {
                  this.app?.patchAppState({
                    showConfirmSaveReportLayoutDialog: false,
                  });
                }}
                cancelButtonColor="white"
                confirmationButtonColor="blue"
                confirmationButtonText={
                  getLocalizedMessage(language, "dialog.button.save") || "Save"
                }
                confirmationButtonFireEvent={async () => {
                  if (!this.app) return;

                  const { pendingSaveReportLayout, reportLayout } =
                    this.app.getAppState();

                  // clear the dialog flag
                  this.app.patchAppState({
                    showConfirmSaveReportLayoutDialog: false,
                  });

                  if (pendingSaveReportLayout) {
                    try {
                      await createViewConfiguration({
                        tabs: pendingSaveReportLayout.tabs,
                        canEdit: reportLayout?.canEdit ?? true,
                        displayTabs: pendingSaveReportLayout.displayTabs,
                        type: pendingSaveReportLayout.type ?? "project-common",
                      });
                      // show success snackbar
                      this.app.patchAppState({
                        snackbar: {
                          message:
                            getLocalizedMessage(
                              language,
                              "layoutSavedSuccessReports"
                            ) ||
                            "Layout saved. Changes have been applied to all report pages.",
                          severity: "success",
                          open: true,
                          type: "snackbar",
                        },
                      });
                    } catch {
                      // show error snackbar
                      this.app.patchAppState({
                        snackbar: {
                          message:
                            getLocalizedMessage(
                              language,
                              "layoutSaveFailedReports"
                            ) ||
                            "Failed to save layout changes. Please try again.",
                          severity: "error",
                          open: true,
                          type: "snackbar",
                        },
                      });
                    }
                  } else {
                    // if not confirmed, revert the in-memory layout to the last saved layout
                    if (reportLayout && reportLayout.tabs) {
                      this.app.patchAppState({ reportLayout });
                    }
                  }

                  // remove pending data
                  this.app.patchAppState({
                    pendingSaveReportLayout: undefined,
                  });
                }}
              />
              {/* TableConfigurrator Confirmation Popup */}
              <PMPopUp
                open={!!confirmationPopupType}
                title={
                  confirmationPopupType === "save"
                    ? getLocalizedMessage(
                        language || "en",
                        "dialog.title.saveChanges"
                      ) || "Save table changes"
                    : getLocalizedMessage(
                        language || "en",
                        "dialog.title.unsavedChanges"
                      ) || "Unsaved Changes"
                }
                description={
                  confirmationPopupType === "save"
                    ? getLocalizedMessage(
                        language || "en",
                        "dialog.message.saveTableConfiguration"
                      ) ||
                      "When saving table configuration, the tables of all other report pages will be adapted as well. Do you want to save these changes for all report pages?"
                    : getLocalizedMessage(
                        language || "en",
                        "dialog.message.abortTableConfiguration"
                      ) ||
                      "You have made changes that have not been saved. If you close the table configurator, these changes will be lost."
                }
                onClose={async () => {
                  const reportTableConfiguration =
                    this.app?.getAppState().reportTableConfiguration;

                  this.app?.patchAppState({
                    reportTableConfiguration: {
                      ...reportTableConfiguration,
                      tableHeaderEntries:
                        reportTableConfiguration?.tableHeaderEntries ?? [],
                      isEditMode: true,
                      confirmationPopupType: undefined,
                    },
                  });
                }}
                maxWidth="sm"
                cancelButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.cancel"
                  ) || "Cancel"
                }
                cancelButtonFireEvent={async () => {
                  const reportTableConfiguration =
                    this.app?.getAppState().reportTableConfiguration;

                  this.app?.patchAppState({
                    reportTableConfiguration: {
                      ...reportTableConfiguration,
                      tableHeaderEntries:
                        reportTableConfiguration?.tableHeaderEntries ?? [],
                      isEditMode: true,
                      confirmationPopupType: undefined,
                    },
                  });
                }}
                cancelButtonColor="white"
                confirmationButtonColor="blue"
                confirmationButtonText={
                  confirmationPopupType === "save"
                    ? getLocalizedMessage(
                        language || "en",
                        "dialog.button.save"
                      ) || "Save"
                    : getLocalizedMessage(
                        language || "en",
                        "dialog.button.continue"
                      ) || "Continue"
                }
                confirmationButtonFireEvent={async () => {
                  if (!this.app) return;

                  if (confirmationPopupType === "save") {
                    // Persist headers in current display order so refresh can rebuild colOrder.
                    const orderedHeadersForSave =
                      applyColumnOrderToHeadersForReportPlugin(
                        this.app
                          ?.getAppState()
                          .reportTableConfiguration?.tableHeaderEntries?.map(
                            (entry) => ({
                              ...entry,
                            })
                          ) ?? [],
                        this.app?.getAppState().reportPluginColumnOrder
                      );

                    await saveTableConfiguration({
                      component: "trackingItemsTableComponent",
                      columns: orderedHeadersForSave,
                    });

                    const identityColumnOrder = orderedHeadersForSave.map(
                      (_, index) => index
                    );

                    this.app?.patchAppState({
                      reportTableConfiguration: {
                        isEditMode: false,
                        tableHeaderEntries: orderedHeadersForSave,
                        isDirty: false,
                      },
                      reportPluginColumnOrder: identityColumnOrder,
                    });

                    return;
                  }

                  const savedConfig = await fetchTableConfiguration(
                    "trackingItemsTableComponent"
                  );

                  this.app?.patchAppState({
                    reportTableConfiguration: {
                      isEditMode: false,
                      tableHeaderEntries: savedConfig?.columns ?? [],
                      isDirty: false,
                    },
                    reportPluginColumnOrder: undefined,
                  });
                }}
              />
              {/* Tracking Items Details Modal */}
              <TrackingItemDetailsModal
                open={!!showTrackingItemDetailsModal}
                language={language}
                canEditItem={canUserPerformOperation({
                  userGroups: userProfile?.groups || [],
                  operation: "edit",
                  object: "item",
                  entityId: currentReport?.project?.id,
                  client: settings?.configuration?.client,
                  tool: settings?.configuration?.tool,
                })}
                onClose={() => {
                  this.app?.patchAppState({
                    showTrackingItemDetailsModal: false,
                    selectedTrackingItem: undefined,
                  });
                }}
                itemLockDetail={
                  selectedItemLock?.isLocked &&
                  selectedItemLock.lockedBy !== userProfile?.email
                    ? selectedItemLock
                    : undefined
                }
                reportingLockDetail={currentProjectLock?.update}
                selectedTrackingItem={selectedTrackingItem}
                configuration={settings?.configuration}
                currentReport={currentReport}
                attachments={selectedTrackingItem?.attachments}
                onDownloadAttachment={downloadAttachment}
                onEditMeasure={(trackingItem) => {
                  // Close the modal first before navigating to the edit form
                  this.app?.patchAppState({
                    showTrackingItemDetailsModal: false,
                    selectedTrackingItem: undefined,
                  });

                  this.app?.patchAppState({
                    editForm: {
                      currentTrackingItem: trackingItem.basicItem,
                    },
                  });

                  this.app?.navigate(
                    generatePath(APP_ROUTES.trackingItemDialog, {
                      id: currentReport?.project?.id ?? "",
                      "tracking-item-id": trackingItem.basicItem?.id ?? "",
                      viewname: "dashboard",
                    })
                  );
                }}
                onUpdateMeasure={() => {
                  // Close the modal
                  this.app?.patchAppState({
                    showTrackingItemDetailsModal: false,
                    selectedTrackingItem: undefined,
                  });

                  // Navigate to update report screen with the specific tab ID
                  this.app?.patchAppState({
                    editForm: { currentProject: currentReport?.project },
                  });

                  const basePath = generatePath(APP_ROUTES.editReport, {
                    id: String(currentReport?.project?.id),
                    viewname: "dashboard",
                  });

                  const tabId = selectedTrackingItem?.basicItem?.id?.toString();
                  const pathWithTab = tabId
                    ? `${basePath}?tabId=${tabId}`
                    : basePath;

                  this.app?.navigate(pathWithTab);
                }}
              />
            </>
          ),
        };
      }
    );

    const view = this.view;
    this.route = app.createRoute<"id" | "viewname">({
      path: "/reports/:id/:viewname",
      view: view,
    });

    buildFormRoute(app, this.route!);
    void buildDimensionFormRoute(app, this.route!);

    void fetchEntityConfiguration().then((configuration) => {
      if (configuration?.useCase === "maturity") {
        buildMaturityFormRoute(app, this.route!);
      }
    });
    buildTrackingItemFormRoute(app, this.route);

    this.view.on("updateLayout", async ({ payload }) => {
      if (!payload || !this.app) return;

      const { reportLayout } = this.app.getAppState();

      const currentDisplayTabs = reportLayout?.displayTabs ?? true;

      const { tabs } = payload as { tabs: DragDropLayoutTabDefinition[] };

      // patch the in-memory layout immediately so the UI reflects the drag-drop action
      this.app.patchAppState({
        reportLayout: {
          displayTabs: currentDisplayTabs,
          tabs,
        },
      });

      // open a confirmation dialog that is rendered in the view rightSlot
      // store the pending tabs in app state so the dialog handler can access them
      this.app.patchAppState({
        pendingSaveReportLayout: {
          tabs,
          displayTabs: currentDisplayTabs,
          type: "project-common",
        },
        showConfirmSaveReportLayoutDialog: true,
      });
    });
    this.view?.on("startLayoutEdit", () => {
      this.app?.patchAppState({
        isSidebarOpen: false,
      });
    });

    this.view?.on("stopLayoutEdit", () => {
      this.app?.patchAppState({
        isSidebarOpen: true,
      });
    });
    this.view.buildDynamicallyBasedOnAppState(
      (appState) => ({
        configuration: appState.settings?.configuration,
        project: appState.currentReport?.project,
      }),

      (state, view) => {
        // static components are built here
        this.addComponentsToLayout(true, state.configuration);
        // dynamic components are built here
        addCustomFieldCards(view, state);
      }
    );
  }

  async addComponentsToLayout(
    includeTrackingItemsTable = true,
    configuration: Configuration | undefined = this.app?.getAppState().settings
      ?.configuration
  ) {
    if (!this.app || !this.view) {
      return;
    }

    const state = this.app.getAppState();
    const language = state.language || "en";
    const { settings } = state;
    const itemName = getItemName(settings?.configuration?.itemName, language);
    const itemNamePlural = getPluralOfItemName(
      settings?.configuration?.itemName,
      language
    );
    const projectName = getProjectName(
      settings?.configuration?.projectName,
      language
    );
    const getCardCopy = (
      currentLanguage: string,
      key: Parameters<typeof getLocalizedCardCopy>[1],
      params: Parameters<typeof getLocalizedCardCopy>[2] = {}
    ) =>
      getLocalizedCardCopy(currentLanguage, key, {
        itemName,
        itemNamePlural,
        projectName,
        ...params,
      });

    for (const addCard of REPORT_CARDS) {
      addCard(this.view, this.app);
    }

    if (includeTrackingItemsTable) {
      const enabledItemCustomFieldsForTable =
        configuration?.customFields?.filter(
          (field) =>
            field?.enabled &&
            field?.flag !== "delete" &&
            (field?.purpose === "itemBasic" ||
              field?.purpose === "itemReporting")
        ) ?? [];

      const baseTrackingItemsTableDefinition = [
        { component: TextCardCell },
        { component: TextCardCell },
        { component: TextCardCell },
        { component: TextCardCell },
        { component: TextCardCell },
        { component: ChipCell },
        { component: StatusIndicatorCell },
        { component: StatusIndicatorCell },
        { component: ProgressBarCell },
        { component: ProgressBarCell },
        { component: DateCell },
        { component: DateCell },
      ];

      const trackingItemsTableDefinition: Array<{
        component:
          | typeof TextCardCell
          | typeof ChipCell
          | typeof StatusIndicatorCell
          | typeof ProgressBarCell
          | typeof DateCell;
      }> = [
        ...baseTrackingItemsTableDefinition,
        ...enabledItemCustomFieldsForTable.map((field) => ({
          component:
            field.type === "Date"
              ? DateCell
              : field.type === "Select"
                ? ChipCell
                : TextCardCell,
        })),
      ];

      const trackingItemsTable = new PMTable<
        typeof trackingItemsTableDefinition
      >(trackingItemsTableDefinition);

      const dropdownFieldOneLabel =
        settings?.configuration?.fieldsDefinition?.find(
          (field) => field.fieldName === "dropdownFieldOne"
        )?.label ??
        getLocalizedMessage(language, "team") ??
        "Team";
      const dimensionLabel =
        settings?.configuration?.fieldsDefinition
          ?.filter((field) => field.purpose === "itemBasic")
          .find((field) => field.fieldName === "dimension")?.label ??
        getLocalizedMessage(language, "dimension") ??
        "Dimension";
      const getTrackingItemsTableColumnLabel = (
        currentLanguage: string,
        fieldName: string
      ) =>
        getLocalizedTableColumnLabel(currentLanguage, fieldName, {
          dropdownFieldOneLabel,
          dimensionLabel,
        });

      const trackingItemsTableComponent = this.view.addComponent<
        DerivePMTableProps<typeof trackingItemsTable>,
        PMTableEventNames,
        PMTableEventPayload
      >({
        component: trackingItemsTable,
        layoutProps: {
          componentId: "trackingItemsTableComponent",
          labels: ["table", "tracking items"],
          description: getCardCopy(language, "reportTrackingItemsTable")
            .description,
          title: getCardCopy(language, "reportTrackingItemsTable").title,
        },
      });

      trackingItemsTableComponent.mapLayoutProps(({ language }) => {
        return {
          componentId: "trackingItemsTableComponent",
          labels: ["table", "tracking items"],
          title: getCardCopy(language, "reportTrackingItemsTable").title,
          description: getCardCopy(language, "reportTrackingItemsTable")
            .description,
        };
      });

      trackingItemsTableComponent.on("sort", (event) => {
        if (!event?.payload || !this.app) {
          return;
        }

        const payload = event?.payload as PMTableSortEventPayload<
          keyof (BasicItem | ReportingItem)
        >;
        const state = this.app.getAppState();
        let direction: SortDirection = "ASC";

        // Swap direction if key is already being sorted by in ascending order.
        if (
          state.reportPluginSortData?.key === payload?.sort?.key &&
          state.reportPluginSortData?.direction === "ASC"
        ) {
          direction = "DESC";
        }

        this.app.patchAppState({
          reportPluginSortData: {
            key: payload?.sort?.key,
            direction,
          },
        });
      });

      trackingItemsTableComponent.mapState(
        ({
          currentReport,
          settings,
          reportPluginSortData,
          reportPluginColumnOrder,
          userProfile,
          isSidebarOpen,
          language,
          reportFilterData,
          reportTableConfiguration,
          lockDetails,
        }) => {
          const resourceLockPlugin =
            this.app?.getPluginByName<ResourceLockPlugin<TestAppState>>(
              "ResourceLockPlugin"
            );

          const reportingItems = sortReportingItems(
            currentReport?.project?.reportingItems?.map((i) => ({
              ...i,
              customId: i.basicItem?.customId,
            })) ?? [],
            reportPluginSortData
          ) as ReportingItem[];

          // Ensure the returned object matches DerivePMTableProps type
          // and that columnProps is a tuple of the correct length and types
          // const reportingDetails = settings?.reportingDetails;
          const isOwnerEnabled =
            settings?.configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemBasic")
              .find((field) => field.fieldName === "owner")?.enabled ?? false;
          const isProgressEnabled = settings?.configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "itemReporting")
            .find((field) => field.fieldName === "progress")?.enabled;
          const isBudgetEnabled = settings?.configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "projectBasic")
            .find((field) => field.fieldName === "budget")?.enabled;

          const isTeamEnabled =
            settings?.configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemBasic")
              .find((field) => field.fieldName === "dropdownFieldOne")
              ?.enabled ?? false;

          const isDimensionEnabled =
            settings?.configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemBasic")
              .find((field) => field.fieldName === "dimension")?.enabled ??
            false;

          const isMaturityEnabled =
            settings?.configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemBasic")
              .find((field) => field.fieldName === "maturity")?.enabled ??
            false;

          const isStatusEnabled =
            settings?.configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemReporting")
              .find((field) => field.fieldName === "status")?.enabled ?? false;

          const isCompletionEnabled =
            settings?.configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemReporting")
              .find((field) => field.fieldName === "completion")?.enabled ??
            false;

          const isStartDateEnabled =
            settings?.configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemBasic")
              .find((field) => field.fieldName === "startDate")?.enabled ??
            false;

          const isEndDateEnabled =
            settings?.configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemBasic")
              .find((field) => field.fieldName === "endDate")?.enabled ?? false;

          const rawTableHeaderEntries =
            reportTableConfiguration?.tableHeaderEntries ?? [];

          const enabledItemCustomFields =
            settings?.configuration?.customFields?.filter(
              (field) =>
                field?.enabled &&
                field?.flag !== "delete" &&
                (field?.purpose === "itemBasic" ||
                  field?.purpose === "itemReporting")
            ) ?? [];

          const customLabelByFieldName = new Map(
            enabledItemCustomFields.flatMap((field) => [
              [getCustomFieldTableColumnFieldName(field), field.label],
              [
                getLegacyCustomFieldTableColumnFieldName(field.label),
                field.label,
              ],
            ])
          );

          const customFieldDefinitionByFieldName = new Map(
            enabledItemCustomFields.flatMap((field) => [
              [getCustomFieldTableColumnFieldName(field), field],
              [getLegacyCustomFieldTableColumnFieldName(field.label), field],
            ])
          );

          const orderedUniqueHeaders = rawTableHeaderEntries.filter(
            (entry, index, source) =>
              source.findIndex((e) => e.fieldName === entry.fieldName) === index
          );

          const orderedUniqueKnownHeaders = orderedUniqueHeaders.filter(
            (entry) =>
              REPORT_TABLE_FIELD_ORDER.includes(
                entry.fieldName as (typeof REPORT_TABLE_FIELD_ORDER)[number]
              )
          );

          const orderedUniqueCustomHeaders = orderedUniqueHeaders
            .filter(
              (entry) =>
                String(entry.fieldName).startsWith("customField-") &&
                customFieldDefinitionByFieldName.has(String(entry.fieldName))
            )
            .map((entry) => {
              const customFieldDefinition =
                customFieldDefinitionByFieldName.get(String(entry.fieldName));

              return {
                ...entry,
                fieldName: customFieldDefinition
                  ? getCustomFieldTableColumnFieldName(customFieldDefinition)
                  : entry.fieldName,
                label:
                  customLabelByFieldName.get(String(entry.fieldName)) ??
                  entry.label,
              };
            });

          const existingCustomFieldNames = new Set(
            orderedUniqueCustomHeaders.map((entry) => String(entry.fieldName))
          );

          const missingEnabledCustomHeaders = enabledItemCustomFields
            .map((field) => {
              const fieldName = getCustomFieldTableColumnFieldName(field);
              if (existingCustomFieldNames.has(fieldName)) {
                return null;
              }

              return {
                fieldName,
                label: field.label,
                isSortable: true,
                isHidden: false,
                sortDirection: null,
              };
            })
            .filter(
              (entry): entry is NonNullable<typeof entry> => entry != null
            );

          const headerByField = new Map(
            orderedUniqueKnownHeaders.map((entry) => [entry.fieldName, entry])
          );

          const normalizedBaseHeaders = REPORT_TABLE_FIELD_ORDER.map(
            (fieldName) => {
              const fromConfig = headerByField.get(fieldName);

              return {
                ...(fromConfig ?? {
                  fieldName,
                  isSortable: true,
                  isHidden: false,
                  sortDirection: null,
                }),
                fieldName,
                label: getTrackingItemsTableColumnLabel(language, fieldName),
              };
            }
          );

          const normalizedHeaders = [
            ...normalizedBaseHeaders,
            ...orderedUniqueCustomHeaders,
            ...missingEnabledCustomHeaders,
          ];

          const normalizedHeaderByFieldName = new Map(
            normalizedHeaders.map((entry) => [String(entry.fieldName), entry])
          );

          // Saved table configuration order is the source of truth.
          // Keep that order, then append any missing columns.
          const savedOrderedFieldNames = orderedUniqueHeaders
            .map((entry) => {
              const rawFieldName = String(entry.fieldName);
              const customFieldDefinition =
                customFieldDefinitionByFieldName.get(rawFieldName);

              return customFieldDefinition
                ? getCustomFieldTableColumnFieldName(customFieldDefinition)
                : rawFieldName;
            })
            .filter(
              (fieldName, index, source) =>
                source.findIndex((value) => value === fieldName) === index
            )
            .filter((fieldName) => normalizedHeaderByFieldName.has(fieldName));

          const missingFieldNames = normalizedHeaders
            .map((entry) => String(entry.fieldName))
            .filter((fieldName) => !savedOrderedFieldNames.includes(fieldName));

          const orderedHeadersForRender = [
            ...savedOrderedFieldNames,
            ...missingFieldNames,
          ].map((fieldName) => normalizedHeaderByFieldName.get(fieldName)!);

          const tableHeaderEntries = generateTableHeaderEntriesWithSorting<
            BasicItem & ReportingItem & { updateStatus?: string }
          >({
            entries: orderedHeadersForRender.map((e) => ({
              ...e,
              fieldName: e.fieldName as keyof (BasicItem &
                ReportingItem & { updateStatus?: string }),
              label: e.label || getLocalizedMessage(language, `${e.fieldName}`),
            })),
            tableSortData: reportPluginSortData,
          });

          const headerVisibilityByField: Record<string, boolean> = {
            customId: true,
            name: true,
            dimension: isDimensionEnabled,
            maturity: isMaturityEnabled,
            owner: isOwnerEnabled,
            dropdownFieldOne: isTeamEnabled,
            status: isStatusEnabled,
            degreeOfCompletion: isCompletionEnabled,
            progress: !!isProgressEnabled,
            budget: !!isBudgetEnabled,
            startDate: isStartDateEnabled,
            endDate: isEndDateEnabled,
          };

          const tableHeaderEntriesWithVisibility = tableHeaderEntries.map(
            (entry) => {
              const isIdColumn = entry.fieldName === "customId";
              const isCustomFieldColumn = String(entry.fieldName).startsWith(
                "customField-"
              );
              const sxObject =
                entry.sx &&
                typeof entry.sx === "object" &&
                !Array.isArray(entry.sx)
                  ? (entry.sx as Record<string, unknown>)
                  : undefined;

              const baseSx = isIdColumn
                ? {
                    ...(sxObject ?? {}),
                    position: "sticky" as const,
                    left: 0,
                    zIndex: 4,
                    backgroundColor: "background.paper",
                  }
                : sxObject
                  ? (() => {
                      const sxWithoutSticky = { ...sxObject };
                      delete sxWithoutSticky.position;
                      delete sxWithoutSticky.left;
                      delete sxWithoutSticky.right;
                      return sxWithoutSticky;
                    })()
                  : entry.sx;

              const isBadgeColumn = [
                "dimension",
                "maturity",
                "dropdownFieldOne",
              ].includes(String(entry.fieldName));

              const normalizedSx =
                isCustomFieldColumn &&
                baseSx &&
                typeof baseSx === "object" &&
                !Array.isArray(baseSx)
                  ? {
                      ...baseSx,
                      minWidth: 220,
                      width: 220,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                    }
                  : isCustomFieldColumn
                    ? {
                        minWidth: 220,
                        width: 220,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                      }
                    : isBadgeColumn
                      ? {
                          ...(baseSx &&
                          typeof baseSx === "object" &&
                          !Array.isArray(baseSx)
                            ? (baseSx as Record<string, unknown>)
                            : {}),
                          width: "200px",
                          minWidth: "200px",
                        }
                      : baseSx;

              return {
                ...entry,
                sx: normalizedSx,
                isHidden:
                  entry.isHidden ||
                  headerVisibilityByField[entry.fieldName] === false,
              };
            }
          );

          const defaultColumnOrder = tableHeaderEntriesWithVisibility.map(
            (_, index) => index
          );

          const isValidColumnOrder = (order: number[] | undefined) => {
            if (
              !order ||
              order.length !== tableHeaderEntriesWithVisibility.length
            ) {
              return false;
            }

            const expected = new Set(defaultColumnOrder);
            const actual = new Set(order);

            if (actual.size !== expected.size) {
              return false;
            }

            return [...expected].every((index) => actual.has(index));
          };

          const effectiveColumnOrder = isValidColumnOrder(
            reportPluginColumnOrder
          )
            ? reportPluginColumnOrder
            : defaultColumnOrder;

          // Seed order once after refresh so drag/reorder state is restored.
          if (!isValidColumnOrder(reportPluginColumnOrder)) {
            this.app?.patchAppState({
              reportPluginColumnOrder: effectiveColumnOrder,
            });
          }

          return {
            title: getLocalizedMessage(language, "overview", {
              overviewItem: getPluralOfItemName(
                settings?.configuration?.itemName,
                language
              ),
            }),
            isSidebarOpen: isSidebarOpen,
            rightSlotOverride: (
              <Box
                sx={{
                  p: 0,
                  display: "flex",
                  marginTop: "12px",
                  flexDirection: "row",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                {canUserPerformOperation({
                  userGroups: userProfile?.groups || [],
                  operation: "edit",
                  object: "item",
                  entityId: currentReport?.project?.id,
                  client: settings?.configuration?.client,
                  tool: settings?.configuration?.tool,
                }) && (
                  <PMButton
                    size="medium"
                    variant="whiteOutlined"
                    icon={<Add />}
                    text={
                      getLocalizedMessage(language, "addDeliverableShort") ||
                      "Add Deliverable"
                    }
                    onClick={() => {
                      this.app?.navigate(
                        generatePath(APP_ROUTES.trackingItemDialog, {
                          id: currentReport?.project?.id ?? "",
                          "tracking-item-id": "new-tracking-item",
                          viewname: "dashboard",
                        })
                      );
                    }}
                  />
                )}
                <SearchAndFilter
                  language={this.app?.getAppState().language || "en"}
                  filterData={reportFilterData}
                  handleChange={(newFilterData) => {
                    this.app?.patchAppState({
                      reportFilterData: newFilterData,
                    });
                  }}
                  clearAllFilters={() => {
                    this.app?.patchAppState({
                      reportFilterData: undefined,
                    });
                  }}
                  options={{
                    status: {
                      isEnabled: isStatusEnabled,
                      options:
                        settings?.configuration?.statusDefinition?.map(
                          (statusDef) => statusDef.name
                        ) || [],
                    },
                    completion: {
                      isEnabled: isCompletionEnabled,
                      options:
                        settings?.configuration?.degreeOfCompletionDefinition?.map(
                          (completionDef) => completionDef.name
                        ) || [],
                    },
                    category: {
                      isEnabled: false,
                      options: [],
                    },
                    updateStatus: {
                      isEnabled: false,
                      options: [],
                    },
                  }}
                ></SearchAndFilter>
                {canUserPerformOperation({
                  userGroups: userProfile?.groups || [],
                  lowestLevelAccessRequiredToPerformThisOperation: "admin",
                  client: settings?.configuration?.client,
                  tool: settings?.configuration?.tool,
                }) &&
                  (this.app?.getAppState().reportTableConfiguration
                    ?.isEditMode ? (
                    <Box
                      sx={{
                        display: "flex",
                        p: 0,
                        gap: 0,
                      }}
                    >
                      <Button
                        aria-label={
                          getLocalizedMessage(language, "saveTableChanges") ||
                          "Save table changes"
                        }
                        sx={{
                          borderRadius: "100%",
                          width: "40px",
                          height: "40px",
                          minWidth: "40px",
                          padding: 0,
                        }}
                        title={
                          getLocalizedMessage(language, "saveTableChanges") ||
                          "Save table changes"
                        }
                        onClick={async () => {
                          const reportTableConfiguration =
                            this.app?.getAppState().reportTableConfiguration;

                          if (!reportTableConfiguration) {
                            return;
                          }

                          this.app?.patchAppState({
                            reportTableConfiguration: {
                              ...reportTableConfiguration,
                              confirmationPopupType: "save",
                            },
                          });
                        }}
                      >
                        <Save sx={{ height: "20px", width: "20px" }} />
                      </Button>
                      <Button
                        aria-label={
                          getLocalizedMessage(
                            language,
                            "closeTableConfiguration"
                          ) || "Close table configuration"
                        }
                        sx={{
                          borderRadius: "100%",
                          width: "40px",
                          height: "40px",
                          minWidth: "40px",
                          padding: 0,
                        }}
                        title={
                          getLocalizedMessage(
                            language,
                            "closeTableConfiguration"
                          ) || "Close table configuration"
                        }
                        onClick={async () => {
                          const reportTableConfiguration =
                            this.app?.getAppState().reportTableConfiguration;

                          if (!reportTableConfiguration) {
                            return;
                          }

                          if (reportTableConfiguration.isDirty) {
                            // Show Confirmation Dialog
                            this.app?.patchAppState({
                              reportTableConfiguration: {
                                ...reportTableConfiguration,
                                confirmationPopupType: "abort",
                              },
                            });

                            return;
                          }

                          // Config is not dirty, just exit edit mode
                          const savedConfig = await fetchTableConfiguration(
                            "trackingItemsTableComponent"
                          );

                          this.app?.patchAppState({
                            reportTableConfiguration: {
                              isEditMode: false,
                              tableHeaderEntries: savedConfig?.columns ?? [],
                              isDirty: false,
                            },
                            reportPluginColumnOrder: undefined,
                          });
                        }}
                      >
                        <Close sx={{ height: "20px", width: "20px" }} />
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      aria-label={
                        getLocalizedMessage(language, "configureTable") ||
                        "Configure table"
                      }
                      sx={{
                        borderRadius: "100%",
                        width: "40px",
                        height: "40px",
                        minWidth: "40px",
                        padding: 0,
                      }}
                      title={
                        getLocalizedMessage(language, "configureTable") ||
                        "Configure table"
                      }
                      onClick={() => {
                        this?.app?.patchAppState({
                          reportTableConfiguration: {
                            isEditMode: true,
                            tableHeaderEntries: tableHeaderEntries.map(
                              (entry) => ({
                                ...entry,
                              })
                            ),
                          },
                        });
                      }}
                    >
                      <Settings sx={{ height: "20px", width: "20px" }} />
                    </Button>
                  ))}
              </Box>
            ),
            showInfoIcon: false,
            showMenuIcon: false,
            allowColumnReorder: true,
            colOrder: effectiveColumnOrder,
            language: language,
            isEditMode: reportTableConfiguration?.isEditMode,
            setColOrder: (order) =>
              this.app?.patchAppState({ reportPluginColumnOrder: order }),
            data: reportingItems
              .filter((item) =>
                doesItemPassFilterCriteria(item, reportFilterData, settings)
              )
              .map((item) => {
                const itemLock =
                  lockDetails?.itemLocks?.[`item-${item.basicItem?.id}`];
                const isLocked =
                  itemLock?.isLocked &&
                  itemLock.lockedBy !== userProfile?.email;

                const itemCustomFields = (item.customFields ?? []) as {
                  value?: unknown;
                  customFieldDefinition?: {
                    label?: string;
                  };
                }[];

                const customFieldValueByFieldName = new Map<string, unknown>();
                const customFieldValueByLabel = new Map<string, unknown>();

                for (const customFieldValue of itemCustomFields) {
                  const fieldName = getCustomFieldTableColumnFieldName(
                    customFieldValue.customFieldDefinition
                  );
                  const labelKey = String(
                    customFieldValue.customFieldDefinition?.label ?? ""
                  )
                    .trim()
                    .toLowerCase();

                  const nextValue = customFieldValue.value;
                  const currentValue =
                    customFieldValueByFieldName.get(fieldName);
                  const currentLabelValue =
                    customFieldValueByLabel.get(labelKey);

                  const hasMeaningfulValue =
                    nextValue != null && String(nextValue).trim() !== "";
                  const currentHasMeaningfulValue =
                    currentValue != null && String(currentValue).trim() !== "";
                  const labelHasMeaningfulValue =
                    currentLabelValue != null &&
                    String(currentLabelValue).trim() !== "";

                  if (!currentHasMeaningfulValue || hasMeaningfulValue) {
                    customFieldValueByFieldName.set(fieldName, nextValue);
                  }

                  if (
                    labelKey &&
                    (!labelHasMeaningfulValue || hasMeaningfulValue)
                  ) {
                    customFieldValueByLabel.set(labelKey, nextValue);
                  }
                }
                const backgroundColor = getDropdownColorValue(
                  settings?.configuration?.dropdownFieldOneDefinition,
                  item.basicItem?.dropdownFieldOne,
                  "#0B2753"
                );
                const contrastTextColor = getContrastColor(
                  backgroundColor,
                  theme.palette.text.primary
                );
                const dimensionConfig =
                  settings?.configuration?.dimensionDefinition?.find(
                    (cfg) => cfg.name === item.basicItem?.dimension
                  );
                const dimensionColor = dimensionConfig?.color ?? "#E1F1E7";
                const maturityConfig =
                  settings?.configuration?.maturityConfiguration?.find(
                    (cfg) => cfg.name === item.basicItem?.maturity
                  );
                const maturityColor = maturityConfig?.color ?? "#E1F1E7";

                const baseCellByFieldName = new Map<
                  string,
                  Record<string, unknown>
                >([
                  [
                    "customId",
                    {
                      component: TextCardCell,
                      body: item.basicItem?.customId?.toString() ?? "",
                      cardProps: {
                        sx: {
                          position: "sticky" as const,
                          left: 0,
                          zIndex: 3,
                          minWidth: 120,
                          width: 120,
                          backgroundColor: "background.paper",
                        },
                      },
                    },
                  ],
                  [
                    "name",
                    {
                      component: TextCardCell,
                      body: item.basicItem?.name ?? "",
                      autoCutBody: true,
                      cardProps: {
                        sx: {
                          zIndex: 3,
                          minWidth: 300,
                          width: 300,
                          backgroundColor: "background.paper",
                        },
                      },
                      tooltipProps: {
                        placement: "right",
                        slotProps: {
                          tooltip: {
                            sx: {
                              backgroundColor: "#fff",
                              color: "#414141",
                              border: "1px solid",
                              borderColor: "background.paper",
                              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                              fontSize: "14px",
                              fontWeight: 400,
                              padding: "12px 16px",
                              borderRadius: "8px",
                            },
                          },
                        },
                      },
                    },
                  ],
                  [
                    "owner",
                    {
                      component: TextCardCell,
                      body: item.basicItem?.owner ?? "",
                    },
                  ],
                  [
                    "dimension",
                    {
                      component: ChipCell,
                      body:
                        dimensionConfig?.name ||
                        item.basicItem?.dimension ||
                        "n/a",
                      chipProps: {
                        sx: {
                          backgroundColor: dimensionColor,
                          color: getContrastColor(
                            dimensionColor,
                            theme.palette.text.primary
                          ),
                          borderRadius: "8px",
                          px: 2,
                        },
                      },
                    },
                  ],
                  [
                    "maturity",
                    {
                      component: ChipCell,
                      body:
                        maturityConfig?.name ||
                        item.basicItem?.maturity ||
                        "n/a",
                      chipProps: {
                        sx: {
                          backgroundColor: maturityColor,
                          color: getContrastColor(
                            maturityColor,
                            theme.palette.text.primary
                          ),
                          borderRadius: "8px",
                          px: 2,
                        },
                      },
                    },
                  ],
                  [
                    "dropdownFieldOne",
                    {
                      component: ChipCell,
                      body: getDropdownLabel(
                        settings?.configuration?.dropdownFieldOneDefinition,
                        item.basicItem?.dropdownFieldOne
                      ),
                      chipProps: {
                        sx: {
                          backgroundColor,
                          color: contrastTextColor,
                          borderRadius: "8px",
                          px: 2,
                        },
                      },
                    },
                  ],
                  [
                    "status",
                    {
                      component: StatusIndicatorCell,
                      body:
                        settings?.configuration?.statusReportingLevel ===
                        "category"
                          ? (item?._calculated?.semaphoreStatus?.name ?? "")
                          : getDropdownLabel(
                              settings?.configuration?.statusDefinition,
                              item?.status
                            ),
                      status:
                        settings?.configuration?.statusReportingLevel ===
                        "category"
                          ? (item._calculated?.semaphoreStatus?.color ?? "")
                          : getDropdownColorValue(
                              settings?.configuration?.statusDefinition,
                              item.status,
                              ""
                            ),
                    },
                  ],
                  [
                    "degreeOfCompletion",
                    {
                      component: StatusIndicatorCell,
                      body: getDropdownLabel(
                        settings?.configuration?.degreeOfCompletionDefinition,
                        item.degreeOfCompletion
                      ),
                      status: getDropdownColorValue(
                        settings?.configuration?.degreeOfCompletionDefinition,
                        item.degreeOfCompletion,
                        ""
                      ),
                    },
                  ],
                  [
                    "progress",
                    {
                      component: ProgressBarCell,
                      header: `${Math.round(item.progress ?? 0)} %`,
                      progress: item.progress ?? 0,
                      progressColor: "#38BDF8",
                    },
                  ],
                  [
                    "budget",
                    {
                      component: ProgressBarCell,
                      header: "",
                      progress: item?.basicItem?.budget ?? 0,
                      progressColor: "lightgrey",
                    },
                  ],
                  [
                    "startDate",
                    {
                      component: DateCell,
                      date: item.basicItem?.startDate
                        ? formatDate(item.basicItem?.startDate)
                        : "N/A",
                    },
                  ],
                  [
                    "endDate",
                    {
                      component: DateCell,
                      date: item.basicItem?.endDate
                        ? formatDate(item.basicItem?.endDate)
                        : "N/A",
                    },
                  ],
                ]);

                const orderedColumnProps = tableHeaderEntriesWithVisibility.map(
                  (header) => {
                    const fieldName = String(header.fieldName);

                    if (fieldName.startsWith("customField-")) {
                      const customFieldDefinition =
                        customFieldDefinitionByFieldName.get(fieldName);

                      const rawValue =
                        customFieldValueByFieldName.get(fieldName) ??
                        customFieldValueByLabel.get(
                          String(header.label ?? "")
                            .trim()
                            .toLowerCase()
                        );

                      const baseState = {
                        isHidden: header.isHidden,
                        isVisible: header.isVisible,
                      };

                      if (customFieldDefinition?.type === "Date") {
                        return {
                          component: DateCell,
                          date: rawValue ? formatDate(String(rawValue)) : "N/A",
                          ...baseState,
                        };
                      }

                      if (customFieldDefinition?.type === "Select") {
                        const selectedLabel = getDropdownLabel(
                          customFieldDefinition.options,
                          String(rawValue ?? "")
                        );
                        const optionColor = getDropdownColorValue(
                          customFieldDefinition.options,
                          String(rawValue ?? ""),
                          "#E1F1E7"
                        );

                        return {
                          component: ChipCell,
                          body: selectedLabel,
                          cardProps: {
                            sx: {
                              minWidth: 220,
                              width: 220,
                              maxWidth: 220,
                              overflow: "visible",
                              paddingLeft: "4px",
                              paddingRight: "4px",
                            },
                          },
                          chipProps: {
                            sx: {
                              backgroundColor: optionColor,
                              color: getContrastColor(
                                optionColor,
                                theme.palette.text.primary
                              ),
                              px: 1.5,
                              borderRadius: "8px",
                              width: "fit-content",
                              maxWidth: "calc(100% - 2px)",
                              minWidth: 0,
                              overflow: "hidden",
                              display: "inline-block",
                              boxSizing: "border-box",
                            },
                          },
                          bodyProps: {
                            sx: {
                              width: "100%",
                              minWidth: 0,
                              overflow: "hidden",
                            },
                          },
                          ...baseState,
                        };
                      }

                      return {
                        component: TextCardCell,
                        body: String(rawValue ?? "n/a"),
                        cutBodyAfter:
                          customFieldDefinition?.type === "Text"
                            ? 51
                            : undefined,
                        ...baseState,
                      };
                    }

                    const baseCell = baseCellByFieldName.get(fieldName);

                    return {
                      ...(baseCell ?? {
                        component: TextCardCell,
                        body: "n/a",
                      }),
                      isHidden: header.isHidden,
                      isVisible: header.isVisible,
                    };
                  }
                );

                return {
                  columnProps: orderedColumnProps as any, // eslint-disable-line
                  rowProps: {
                    cardProps: { sx: { cursor: "pointer" } },
                    onClick: () => {
                      // Open tracking item details modal
                      this.app?.patchAppState({
                        showTrackingItemDetailsModal: true,
                        selectedTrackingItem: item,
                      });
                    },
                    lockMenu: isLocked ?? false,
                    menuTooltip: getLockedByMessage("item", language, itemLock),
                    onMenuOpen: canUserPerformOperation({
                      userGroups: userProfile?.groups ?? [],
                      object: "item",
                      operation: "edit",
                      entityId: item.project?.id,
                      lowestLevelAccessRequiredToPerformThisOperation: item
                        .basicItem?.projectSpecificItem
                        ? "project"
                        : "program",
                      client: settings?.configuration?.client,
                      tool: settings?.configuration?.tool,
                    })
                      ? {
                          onEdit: async (itemIndex) => {
                            const basicItem =
                              reportingItems?.[itemIndex]?.basicItem;

                            this.app?.patchAppState({
                              editForm: {
                                currentTrackingItem: basicItem,
                              },
                            });
                            basicItem &&
                              resourceLockPlugin?.lock(`item-${basicItem.id}`);
                            this.app?.navigate(
                              generatePath(APP_ROUTES.trackingItemDialog, {
                                id: currentReport?.project?.id ?? "",
                                "tracking-item-id": basicItem?.id ?? "",
                                viewname:
                                  getLocalizedMessage(language, "dashboard") ||
                                  "dashboard",
                              })
                            );
                          },
                          onDelete: async (index) => {
                            const basicItem =
                              currentReport?.project?.reportingItems?.find(
                                (_, itemIndex) => itemIndex === index
                              )?.basicItem;

                            // set flags in app state so the ConfirmationDialog can show
                            this.app?.patchAppState({
                              showDeleteTrackingItemDialog: true,
                              pendingDeleteTrackingItemId: basicItem!.id,
                            });
                          },
                        }
                      : undefined,
                  },
                };
              }),
            tableHeaderEntries: tableHeaderEntriesWithVisibility,
          };
        }
      );

      trackingItemsTableComponent.on("reorderColumns", () => {
        if (!this.app) {
          return;
        }

        const state = this.app.getAppState();

        const currentConfig = state.reportTableConfiguration;

        this.app.patchAppState({
          reportTableConfiguration: {
            ...currentConfig,
            tableHeaderEntries: currentConfig?.tableHeaderEntries ?? [],
            isEditMode: true,
            isDirty: true,
          },
        });
      });

      trackingItemsTableComponent.on("addColumn", (event) => {
        if (!event?.payload || !this.app) {
          return;
        }

        const payload = event?.payload as PMTableColumnActionEventPayload;
        const state = this.app.getAppState();

        const currentConfig = state.reportTableConfiguration;
        const nextHeaders =
          payload.headers ?? currentConfig?.tableHeaderEntries ?? [];

        this.app.patchAppState({
          reportTableConfiguration: {
            ...currentConfig,
            tableHeaderEntries: nextHeaders,
            isEditMode: true,
            isDirty: true,
          },
        });
      });

      trackingItemsTableComponent.on("deleteColumn", (event) => {
        if (!event?.payload || !this.app) {
          return;
        }

        const payload = event?.payload as PMTableColumnActionEventPayload;
        const state = this.app.getAppState();
        const currentConfig = state.reportTableConfiguration;
        const newTableHeaderEntries =
          payload.headers ?? currentConfig?.tableHeaderEntries ?? [];

        this.app.patchAppState({
          reportTableConfiguration: {
            ...currentConfig,
            tableHeaderEntries: newTableHeaderEntries,
            isEditMode: true,
            isDirty: true,
          },
        });
      });
    }
  }
}

const doesItemPassFilterCriteria = (
  item: ReportingItem,
  filterData: FilterData | undefined,
  settings: TestAppState["settings"] | undefined
): boolean => {
  if (!filterData) {
    return true;
  }

  const selectedStatuses = (filterData.statusFilter ?? [])
    .map((v) => v.trim())
    .filter(Boolean);
  const selectedCompletions = (filterData.completionFilter ?? [])
    .map((v) => v.trim())
    .filter(Boolean);
  const searchQuery = (filterData.searchQueryFilter ?? []).join(" ").trim();

  const statusForDisplay =
    settings?.configuration?.statusReportingLevel === "category"
      ? (item?._calculated?.semaphoreStatus?.name ?? "")
      : (item?.status ?? "");

  const statusCandidates = [statusForDisplay, item?.status ?? ""]
    .map((v) => v.trim())
    .filter(Boolean);

  if (
    selectedStatuses.length > 0 &&
    !statusCandidates.some((status) => selectedStatuses.includes(status))
  ) {
    return false;
  }

  const completionValue = (item.degreeOfCompletion ?? "").trim();
  if (
    selectedCompletions.length > 0 &&
    !selectedCompletions.includes(completionValue)
  ) {
    return false;
  }

  const normalizedQuery = searchQuery.toLowerCase();
  if (normalizedQuery.length > 0) {
    const name = (item.basicItem?.name ?? "").toLowerCase();
    const customId = (item.basicItem?.customId?.toString() ?? "").toLowerCase();
    const owner = (item.basicItem?.owner ?? "").toLowerCase();

    const matches =
      name.includes(normalizedQuery) ||
      customId.includes(normalizedQuery) ||
      owner.includes(normalizedQuery);

    if (!matches) {
      return false;
    }
  }

  return true;
};
